import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowLeft, AlertCircle } from "lucide-react";
import { BrandLogo } from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { HERO_BG } from "@/lib/brand";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="mb-10"><BrandLogo /></div>
        <div className="max-w-sm w-full">
          {!token ? (
            <div data-testid="reset-notoken">
              <div className="h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center"><AlertCircle className="h-6 w-6 text-amber-500" /></div>
              <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">Invalid reset link</h1>
              <p className="text-slate-500 mt-2 text-sm">This link is missing or malformed. Please request a new password reset.</p>
              <Link to="/forgot-password" className="mt-6 inline-block"><Button className="bg-blue-600 hover:bg-blue-700" data-testid="reset-request-new">Request a new link</Button></Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done">
              <div className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center"><CheckCircle2 className="h-6 w-6 text-green-500" /></div>
              <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">Password reset</h1>
              <p className="text-slate-500 mt-2 text-sm">Your password has been updated. Taking you to the login page…</p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium">Go to log in now</Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-slate-900">Set a new password</h1>
              <p className="text-slate-500 mt-2">Choose a strong password you'll remember.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error">{error}</div>}
                <div>
                  <Label htmlFor="password">New password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm" className="mt-1" />
                </div>
                <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11">
                  {loading ? "Saving…" : "Reset password"}
                </Button>
              </form>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-600"><ArrowLeft className="h-4 w-4" /> Back to log in</Link>
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(22,48,92,0.85), rgba(37,99,235,0.55))" }} />
      </div>
    </div>
  );
};

export default ResetPassword;
